import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';

import Sidebar from '../components/Sidebar';
import BuilderTour from '../components/BuilderTour';
import BuilderChatPanel from '../components/BuilderChatPanel';
import UniverseContext from '../components/nodes/UniverseContext';
import SymbolSuggestion from '../components/SymbolSuggestion';
import TickerAutocomplete from '../components/TickerAutocomplete';
import IndicatorNode from '../components/nodes/IndicatorNode';
import ConditionNode from '../components/nodes/ConditionNode';
import ActionNode from '../components/nodes/ActionNode';
import PriceNode from '../components/nodes/PriceNode';
import ValueNode from '../components/nodes/ValueNode';
import LogicNode from '../components/nodes/LogicNode';
import DataNode from '../components/nodes/DataNode';
import CorrelationNode from '../components/nodes/CorrelationNode';
import TimeNode from '../components/nodes/TimeNode';
import LookbackNode from '../components/nodes/LookbackNode';
import TimeframeNode from '../components/nodes/TimeframeNode';
import FibonacciNode from '../components/nodes/FibonacciNode';
import TrendlineNode from '../components/nodes/TrendlineNode';
import PatternNode from '../components/nodes/PatternNode';
import VolatilityNode from '../components/nodes/VolatilityNode';
import PositionAgeNode from '../components/nodes/PositionAgeNode';
import StopLossNode from '../components/nodes/StopLossNode';
import TakeProfitNode from '../components/nodes/TakeProfitNode';
import GapNode from '../components/nodes/GapNode';
import StreakNode from '../components/nodes/StreakNode';
import DivergenceNode from '../components/nodes/DivergenceNode';
import PivotNode from '../components/nodes/PivotNode';
import RelativeStrengthNode from '../components/nodes/RelativeStrengthNode';
import RegimeNode from '../components/nodes/RegimeNode';
import EarningsNode from '../components/nodes/EarningsNode';
import SwingNode from '../components/nodes/SwingNode';
import StructureNode from '../components/nodes/StructureNode';
import LiquiditySweepNode from '../components/nodes/LiquiditySweepNode';
import OrderBlockNode from '../components/nodes/OrderBlockNode';
import FvgNode from '../components/nodes/FvgNode';
import OteNode from '../components/nodes/OteNode';
import SessionRangeNode from '../components/nodes/SessionRangeNode';
import VolumeProfileNode from '../components/nodes/VolumeProfileNode';
import RoundLevelNode from '../components/nodes/RoundLevelNode';
import withNodeDelete from '../components/nodes/withNodeDelete';
import ExportStrategyModal from '../components/ExportStrategyModal';
import { createStrategy, updateStrategy, explainStrategyBrief, generateStrategy, getStrategyPaperSessions, duplicateStrategy, validateSymbol } from '../api';
import { useAuth } from '../context/AuthContext';
import { track, Events } from '../analytics';
import Spinner from '../components/Spinner';

const rawNodeTypes = {
  indicator: IndicatorNode,
  condition: ConditionNode,
  action: ActionNode,
  price: PriceNode,
  value: ValueNode,
  logic: LogicNode,
  data: DataNode,
  correlation: CorrelationNode,
  time: TimeNode,
  lookback: LookbackNode,
  timeframe: TimeframeNode,
  fibonacci: FibonacciNode,
  trendline: TrendlineNode,
  pattern: PatternNode,
  volatility: VolatilityNode,
  position_age: PositionAgeNode,
  stop_loss: StopLossNode,
  take_profit: TakeProfitNode,
  gap: GapNode,
  streak: StreakNode,
  divergence: DivergenceNode,
  pivot: PivotNode,
  relative_strength: RelativeStrengthNode,
  regime: RegimeNode,
  earnings: EarningsNode,
  swing: SwingNode,
  structure: StructureNode,
  liquidity_sweep: LiquiditySweepNode,
  order_block: OrderBlockNode,
  fvg: FvgNode,
  ote: OteNode,
  session_range: SessionRangeNode,
  volume_profile: VolumeProfileNode,
  round_level: RoundLevelNode,
};

// Wrap every node type with a corner delete (×) button (mobile-friendly deletion).
// Built once at module scope so the nodeTypes reference stays stable across renders.
const customNodeTypes = Object.fromEntries(
  Object.entries(rawNodeTypes).map(([type, Comp]) => [type, withNodeDelete(Comp)])
);

let nodeId = 0;
const getNodeId = () => `node_${++nodeId}`;

// Re-attaches the onChange callback each node component uses to edit its own
// params in place — required after any bulk graph replacement (AI generate,
// chat panel updates) since the callback function isn't part of the
// serialized graph.
function attachOnChange(nodes, onChange) {
  return (nodes || []).map((n) => ({
    ...n,
    data: { ...n.data, onChange },
  }));
}

// Mirrors strategies/engine.py:check_graph_completeness — a graph only ever
// trades if it has a buy action AND a sell/close action, each wired to a
// condition (both inputs connected) or logic gate (>=2 inputs). Kept in sync
// manually since this is a small, static graph-shape check with no backend
// dependency; the server re-checks the same thing on save regardless.
function checkGraphCompleteness(graph) {
  const nodesById = {};
  (graph.nodes || []).forEach((n) => { nodesById[n.id] = n; });

  const conditionInputs = {};
  const logicInputs = {};
  const actionSources = {};
  const actionRiskExit = new Set(); // action ids with a stop_loss/take_profit wired in

  (graph.nodes || []).forEach((n) => {
    if (n.type === 'condition') conditionInputs[n.id] = { left: null, right: null };
    else if (n.type === 'logic') logicInputs[n.id] = [];
    else if (n.type === 'action') actionSources[n.id] = [];
  });

  (graph.edges || []).forEach((e) => {
    const targetNode = nodesById[e.target];
    if (!targetNode) return;
    if (targetNode.type === 'condition' && conditionInputs[e.target]) {
      if (e.targetHandle === 'left') conditionInputs[e.target].left = e.source;
      else if (e.targetHandle === 'right') conditionInputs[e.target].right = e.source;
    } else if (targetNode.type === 'logic' && logicInputs[e.target]) {
      logicInputs[e.target].push(e.source);
    } else if (targetNode.type === 'action' && actionSources[e.target]) {
      // A stop_loss/take_profit wired into an entry action IS an exit path.
      if (e.targetHandle === 'stop_loss' || e.targetHandle === 'take_profit') {
        actionRiskExit.add(e.target);
      } else {
        actionSources[e.target].push(e.source);
      }
    }
  });

  const isValidTrigger = (nodeId) => {
    const node = nodesById[nodeId];
    if (!node) return false;
    if (node.type === 'condition') {
      const inputs = conditionInputs[nodeId] || {};
      return inputs.left != null && inputs.right != null;
    }
    if (node.type === 'logic') {
      return new Set(logicInputs[nodeId] || []).size >= 2;
    }
    return false;
  };

  const triggeredActionTypes = new Set();
  let triggeredEntryHasRiskExit = false;
  Object.entries(actionSources).forEach(([aid, srcs]) => {
    if (srcs.some(isValidTrigger)) {
      const atype = (nodesById[aid].data || {}).action;
      triggeredActionTypes.add(atype);
      // A triggered entry (buy/short) with a stop-loss or take-profit exits via
      // that risk order — no separate sell/close action needed.
      if ((atype === 'buy' || atype === 'short') && actionRiskExit.has(aid)) {
        triggeredEntryHasRiskExit = true;
      }
    }
  });

  const hasBuy = triggeredActionTypes.has('buy') || triggeredActionTypes.has('short');
  const hasExit = triggeredActionTypes.has('sell') || triggeredActionTypes.has('cover')
    || triggeredActionTypes.has('close') || triggeredEntryHasRiskExit;

  const reasons = [];
  if (!hasBuy) reasons.push('No buy/short action is wired to a condition or logic gate with valid inputs.');
  if (!hasExit) reasons.push('No exit is defined — add a sell/cover/close action wired to a condition, or a stop-loss/take-profit on the entry action.');

  return { isComplete: hasBuy && hasExit, reasons };
}

export default function StrategyBuilder({ strategy, onSaved, onFeedback, onRunBacktest, onViewResults }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const tapAddCountRef = useRef(0);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Activation funnel: capture each time the builder is opened (new vs editing existing).
  useEffect(() => {
    track(Events.BUILDER_OPENED, { mode: strategy?.id ? 'edit' : 'new' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialNodes = attachOnChange(strategy?.graph?.nodes, handleNodeDataChange);
  const initialEdges = strategy?.graph?.edges || [];

  const [nodes, setNodes, _onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, _onEdgesChange] = useEdgesState(initialEdges);
  const [strategyName, setStrategyName] = useState(strategy?.name || '');
  // Multi-asset universe: secondary { symbol, asset_type } this strategy also
  // trades. Empty => ordinary single-symbol strategy. Persisted on the graph.
  const [universe, setUniverse] = useState(strategy?.graph?.universe || []);
  const [newSymbol, setNewSymbol] = useState('');
  const [newSymbolAsset, setNewSymbolAsset] = useState('stock');
  const [addCheck, setAddCheck] = useState({ status: 'idle', msg: '' }); // ticker verify
  const [isDirty, setIsDirty] = useState(false);
  const [showExport, setShowExport] = useState(false);

  // Warn before browser reload/close when there are unsaved canvas changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Change-tracking wrappers
  const onNodesChange = useCallback((changes) => {
    _onNodesChange(changes);
    if (changes.some((c) => c.type !== 'select')) setIsDirty(true);
  }, [_onNodesChange]);

  const onEdgesChange = useCallback((changes) => {
    _onEdgesChange(changes);
    if (changes.some((c) => c.type !== 'select')) setIsDirty(true);
  }, [_onEdgesChange]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [briefExplanation, setBriefExplanation] = useState(null);
  const [fromCodeGen, setFromCodeGen] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [cannotBuildInfo, setCannotBuildInfo] = useState(null); // {detail, prompt}
  // Paper trading warning: {sessions: [...]} when saving would break active sessions
  const [paperWarn, setPaperWarn] = useState(null);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);
  // Incomplete-graph warning: {reasons: [...]} shown before saving a graph that will never trade
  const [incompleteWarn, setIncompleteWarn] = useState(null);
  // Bumped to force the guided tour to (re)start, e.g. from the "?" help button
  const [tourSignal, setTourSignal] = useState(0);
  // Conversational AI builder: chat panel replaces the classic AI Generate/Modify
  // panel when active. highlightedNodeIds briefly pulses nodes the chat just changed.
  const [chatMode, setChatMode] = useState(false);
  const [highlightedNodeIds, setHighlightedNodeIds] = useState([]);
  const highlightTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
  }, []);

  function handleNodeDataChange(id, field, value) {
    setIsDirty(true);
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== id) return n;
        return { ...n, data: { ...n.data, [field]: value, onChange: handleNodeDataChange } };
      })
    );
  }

  const onConnect = useCallback(
    (params) => { setEdges((eds) => addEdge(params, eds)); setIsDirty(true); },
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow-type');
      const defaults = JSON.parse(event.dataTransfer.getData('application/reactflow-defaults') || '{}');

      if (!type || !reactFlowInstance) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: getNodeId(),
        type,
        position,
        data: { ...defaults, onChange: handleNodeDataChange },
      };

      setNodes((nds) => nds.concat(newNode));
      setIsDirty(true);
    },
    [reactFlowInstance, setNodes]
  );

  // Tap-to-add fallback for touch devices, where native HTML5 drag-and-drop
  // (used by the sidebar) doesn't fire at all. Drops the node near the center
  // of the current viewport, nudging each successive tap so repeats don't stack.
  const handleAddNode = useCallback(
    (type, defaults) => {
      if (!reactFlowInstance || !reactFlowWrapper.current) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const offset = (tapAddCountRef.current % 6) * 24;
      tapAddCountRef.current += 1;

      const position = reactFlowInstance.screenToFlowPosition({
        x: bounds.left + bounds.width / 2 + offset,
        y: bounds.top + bounds.height / 2 + offset,
      });

      const newNode = {
        id: getNodeId(),
        type,
        position,
        data: { ...defaults, onChange: handleNodeDataChange },
      };

      setNodes((nds) => nds.concat(newNode));
      setIsDirty(true);
    },
    [reactFlowInstance, setNodes]
  );

  const buildGraph = () => {
    const graphNodes = nodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: { ...n.data, onChange: undefined },
    }));
    // Remove the onChange function before serializing
    graphNodes.forEach((n) => delete n.data.onChange);
    return { nodes: graphNodes, edges, universe };
  };

  const addUniverseSymbol = async () => {
    const sym = (newSymbol || '').trim().toUpperCase();
    if (!sym) return;
    if (universe.some((u) => u.symbol === sym)) { setNewSymbol(''); setAddCheck({ status: 'idle', msg: '' }); return; }
    // Verify the ticker is real/tradeable before adding it to the strategy.
    setAddCheck({ status: 'checking', msg: '' });
    try {
      const { data } = await validateSymbol(sym, newSymbolAsset);
      if (!data?.valid) {
        setAddCheck({ status: 'invalid', msg: data?.error || `${sym} doesn't look like a valid ${newSymbolAsset} symbol.`, data });
        return;
      }
    } catch {
      // Couldn't verify (offline, or guest without permission) — add it anyway
      // rather than block building; the backtest still validates server-side.
    }
    setUniverse((u) => [...u, { symbol: sym, asset_type: newSymbolAsset }]);
    setNewSymbol('');
    setAddCheck({ status: 'idle', msg: '' });
    setIsDirty(true);
  };

  const removeUniverseSymbol = (sym) => {
    setUniverse((u) => u.filter((x) => x.symbol !== sym));
    setIsDirty(true);
  };

  // Make a symbol the primary by moving it to the front (index 0 = primary).
  const setPrimarySymbol = (sym) => {
    setUniverse((u) => {
      const idx = u.findIndex((x) => x.symbol === sym);
      if (idx <= 0) return u;
      const copy = [...u];
      const [item] = copy.splice(idx, 1);
      copy.unshift(item);
      return copy;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!strategyName.trim()) {
      setSaveError('Please enter a strategy name.');
      return;
    }
    const graph = buildGraph();
    const { isComplete, reasons } = checkGraphCompleteness(graph);
    if (!isComplete) {
      setIncompleteWarn({ reasons });
      return;
    }
    await saveGraph(graph);
  };

  // Called directly from "Save Anyway" on the incomplete-graph warning dialog
  const handleSaveIncompleteAnyway = () => {
    setIncompleteWarn(null);
    saveGraph(buildGraph());
  };

  const saveGraph = async (graph) => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = { name: strategyName, graph };
      if (strategy?.id) {
        // Check if this strategy is in any active paper trading sessions.
        // If the check fails (network error, etc.) we fall through and save normally.
        let activeSessions = [];
        try {
          const { data } = await getStrategyPaperSessions(strategy.id);
          activeSessions = data;
        } catch { /* non-blocking */ }

        if (activeSessions.length > 0) {
          setSaving(false);
          setPendingSavePayload(payload);
          setPaperWarn({ sessions: activeSessions });
          return;
        }
        const resp = await updateStrategy(strategy.id, payload);
        if (onSaved) { setIsDirty(false); onSaved(resp.data); }
      } else {
        const resp = await createStrategy(payload);
        if (onSaved) { setIsDirty(false); onSaved(resp.data); }
      }
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  // Called when user confirms "update anyway" from the paper session warning dialog
  const handleForceUpdate = async () => {
    if (!pendingSavePayload) return;
    setSaving(true);
    setSaveError('');
    setPaperWarn(null);
    try {
      const resp = await updateStrategy(strategy.id, pendingSavePayload);
      setPendingSavePayload(null);
      if (onSaved) { setIsDirty(false); onSaved(resp.data); }
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  // Called when user picks "Duplicate & Edit" — copies the strategy and opens the copy
  const handleDuplicateAndEdit = async () => {
    if (!pendingSavePayload) return;
    setSaving(true);
    setSaveError('');
    setPaperWarn(null);
    try {
      const { data: copy } = await duplicateStrategy(strategy.id, { name: `${strategyName} (Copy)` });
      // Now save the user's edits onto the copy
      const resp = await updateStrategy(copy.id, pendingSavePayload);
      setPendingSavePayload(null);
      if (onSaved) { setIsDirty(false); onSaved(resp.data); }
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message || 'Failed to duplicate.');
    } finally {
      setSaving(false);
    }
  };

  const handleExplain = async () => {
    if (!strategy?.id) {
      setSaveError('Save the strategy first before explaining.');
      return;
    }
    setExplaining(true);
    setBriefExplanation(null);
    setSaveError('');
    try {
      const resp = await explainStrategyBrief(strategy.id);
      setBriefExplanation(resp.data.explanation);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setSaveError(msg);
    } finally {
      setExplaining(false);
    }
  };

  const hasExistingGraph = nodes.length > 0;

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    setBriefExplanation(null);
    setFromCodeGen(false);
    setSaveError('');
    try {
      const payload = { prompt: aiPrompt };
      if (hasExistingGraph) {
        payload.graph = buildGraph();
      }
      const resp = await generateStrategy(payload);
      const { graph, name, explanation } = resp.data;

      setNodes(attachOnChange(graph.nodes, handleNodeDataChange));
      setEdges(graph.edges || []);
      // Adopt the AI's multi-asset universe (present only for multi-symbol builds).
      if (Array.isArray(graph.universe)) setUniverse(graph.universe);
      if (!hasExistingGraph && name) setStrategyName(name);
      if (explanation) setBriefExplanation(explanation);
      setFromCodeGen(!!resp.data.from_code);
      setAiPrompt('');
      setShowAiPanel(false);
      setIsDirty(true); // AI-generated graph is unsaved
    } catch (err) {
      const data = err.response?.data || {};
      if (data.cannot_build) {
        setCannotBuildInfo({ detail: data.detail, prompt: aiPrompt });
        setShowAiPanel(false);
      } else {
        setSaveError(err.response?.data?.detail || err.message || 'Failed to generate strategy.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleChatGraphUpdate = useCallback((graph, name, diff) => {
    setNodes(attachOnChange(graph.nodes, handleNodeDataChange));
    setEdges(graph.edges || []);
    if (Array.isArray(graph.universe)) setUniverse(graph.universe);
    setIsDirty(true);

    const changedIds = diff?.changed_node_ids || [];
    if (changedIds.length > 0) {
      setHighlightedNodeIds(changedIds);
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = setTimeout(() => setHighlightedNodeIds([]), 4000);
    }
    setStrategyName((prev) => (prev.trim() ? prev : name || prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNodes, setEdges]);

  const displayNodes = highlightedNodeIds.length > 0
    ? nodes.map((n) => (highlightedNodeIds.includes(n.id) ? { ...n, className: 'node-recently-changed' } : n))
    : nodes;

  return (
   <UniverseContext.Provider value={universe}>
    <div className="builder-layout" style={{ display: 'flex', height: '100%' }}>
      <BuilderTour showAiStep={isAuthenticated} runSignal={tourSignal} />
      {!(chatMode && isMobile) && <Sidebar onAddNode={handleAddNode} />}
      <Helmet>
        <title>Strategy Builder — Algovex</title>
        <meta name="description" content="Drag-and-drop algorithmic trading strategy builder with AI-powered generation. Build and backtest strategies for stocks, forex, and crypto." />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* ── Paper Trading Warning Dialog ── */}
      {paperWarn && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
            padding: 28, maxWidth: 460, width: '100%',
          }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>⚠️ Active Paper Trade</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: '0 0 16px', lineHeight: 1.6 }}>
              This strategy is currently used in{' '}
              <strong style={{ color: 'var(--text)' }}>
                {paperWarn.sessions.length} active paper trading session{paperWarn.sessions.length > 1 ? 's' : ''}
              </strong>:
            </p>
            <ul style={{ margin: '0 0 20px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>
              {paperWarn.sessions.map((s) => (
                <li key={s.id}><strong style={{ color: 'var(--text)' }}>{s.name}</strong> — {s.symbol} ({s.status})</li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 20px', lineHeight: 1.5 }}>
              Updating the strategy will affect how those sessions run.{' '}
              <strong style={{ color: 'var(--text)' }}>Duplicate &amp; Edit</strong> creates a new strategy with your changes,
              leaving the paper trades untouched.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={handleDuplicateAndEdit}
                disabled={saving}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: 'var(--teal)', color: 'var(--bg)', border: 'none',
                }}
              >
                {saving ? 'Working…' : 'Duplicate & Edit'}
              </button>
              <button
                onClick={handleForceUpdate}
                disabled={saving}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: 'var(--surface)', color: 'var(--red)', border: '1px solid var(--red)',
                }}
              >
                Update Anyway
              </button>
              <button
                onClick={() => { setPaperWarn(null); setPendingSavePayload(null); }}
                style={{
                  width: '100%', padding: '8px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                  background: 'none', color: 'var(--muted)', border: '1px solid var(--border)',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Incomplete Graph Warning Dialog ── */}
      {incompleteWarn && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
            padding: 28, maxWidth: 460, width: '100%',
          }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>⚠️ Incomplete Strategy</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: '0 0 16px', lineHeight: 1.6 }}>
              This strategy doesn't have a complete buy/sell path yet, so it will never open or close a position in a backtest:
            </p>
            <ul style={{ margin: '0 0 20px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>
              {incompleteWarn.reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => setIncompleteWarn(null)}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: 'var(--teal)', color: 'var(--bg)', border: 'none',
                }}
              >
                Keep Editing
              </button>
              <button
                onClick={handleSaveIncompleteAnyway}
                disabled={saving}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: 'var(--surface)', color: 'var(--red)', border: '1px solid var(--red)',
                }}
              >
                {saving ? 'Working…' : 'Save Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <div className="builder-topbar" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
        }}>
          <input
            data-tour="strategy-name"
            value={strategyName}
            onChange={(e) => setStrategyName(e.target.value)}
            placeholder="Strategy name…"
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: 14,
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: 'var(--card)',
              color: 'var(--text)',
            }}
          />
          <button
            onClick={() => setTourSignal((s) => s + 1)}
            title="Replay the builder tutorial"
            style={{
              padding: '8px 12px',
              background: 'var(--card)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            ❓ Help
          </button>
          <button
            data-tour="save-button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 20px',
              background: 'var(--teal)',
              color: 'var(--bg)',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {saving ? 'Saving…' : strategy?.id ? 'Update Strategy' : 'Save Strategy'}
          </button>
          {strategy?.id && (
            <button
              onClick={handleExplain}
              disabled={explaining}
              style={{
                padding: '8px 16px',
                background: 'var(--blue)',
                color: 'var(--bg)',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {explaining ? <><Spinner size={11} color="var(--bg)" style={{ marginRight: 6 }} />Explaining…</> : '💡 Explain'}
            </button>
          )}
          {isAuthenticated && (
            <button
              onClick={() => { track(Events.STRATEGY_EXPORT_OPENED, { source: 'builder' }); setShowExport(true); }}
              title="Export this strategy to backtrader / PineScript code"
              style={{
                padding: '8px 16px',
                background: 'var(--card)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              ⬇ Export code
            </button>
          )}
          {isAuthenticated && !chatMode && (
            <button
              data-tour="ai-generate"
              onClick={() => setShowAiPanel((v) => !v)}
              style={{
                padding: '8px 16px',
                background: showAiPanel ? 'var(--amber)' : 'var(--card)',
                color: showAiPanel ? 'var(--bg)' : 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {hasExistingGraph ? '✨ AI Modify' : '✨ AI Generate'}
            </button>
          )}
          {isAuthenticated && (
            <button
              onClick={() => { setChatMode((v) => !v); setShowAiPanel(false); }}
              title="Build and refine your strategy through a back-and-forth conversation"
              style={{
                padding: '8px 16px',
                background: chatMode ? 'var(--teal)' : 'var(--card)',
                color: chatMode ? 'var(--bg)' : 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              💬 AI Chat
            </button>
          )}
        </div>

        {/* ── Multi-asset universe editor ──
            The full list of symbols this strategy trades. The first is the
            PRIMARY (● badge). When non-empty, every price/indicator/action node
            gains a symbol dropdown (via UniverseContext) to bind it to a
            specific instrument. Leave empty for an ordinary single-symbol
            strategy (you pick the ticker when you run it). */}
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          padding: '8px 16px', background: 'var(--surface)',
          borderBottom: '1px solid var(--border)', fontSize: 12,
        }}>
          <span style={{ color: 'var(--text)', fontWeight: 700 }} title="Every symbol this strategy trades. The first (● badge) is the primary; nodes default to it. Add ≥2 for a multi-asset strategy, or leave empty to run on any single symbol you choose at run time.">
            Symbols traded:
          </span>
          {universe.length === 0 && (
            <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
              none yet — single-symbol (chosen at run time). Add symbols to go multi-asset.
            </span>
          )}
          {universe.map((u, i) => (
            <span key={u.symbol} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '2px 8px', borderRadius: 12,
              background: i === 0 ? 'var(--accent, #6366f1)' : 'var(--card)',
              border: '1px solid var(--border)',
              color: i === 0 ? '#fff' : 'var(--text)',
            }}>
              {i === 0 && <span title="Primary symbol">●</span>}
              {u.symbol}
              {i !== 0 && (
                <button
                  onClick={() => setPrimarySymbol(u.symbol)}
                  title={`Make ${u.symbol} the primary`}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0, fontSize: 11 }}
                >★</button>
              )}
              <button
                onClick={() => removeUniverseSymbol(u.symbol)}
                title={`Remove ${u.symbol}`}
                style={{ background: 'none', border: 'none', color: i === 0 ? '#fff' : 'var(--muted)', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}
              >×</button>
            </span>
          ))}
          <select
            value={newSymbolAsset}
            onChange={(e) => { setNewSymbolAsset(e.target.value); setAddCheck({ status: 'idle', msg: '' }); }}
            title="Asset type of the symbol you're adding"
            style={{
              padding: '4px 6px', fontSize: 12,
              border: '1px solid var(--border)', borderRadius: 6,
              background: 'var(--card)', color: 'var(--text)',
            }}
          >
            <option value="stock">Stock</option>
            <option value="fx">Forex</option>
            <option value="crypto">Crypto</option>
          </select>
          <TickerAutocomplete
            value={newSymbol}
            onChange={(val) => { setNewSymbol(val); if (addCheck.status !== 'idle') setAddCheck({ status: 'idle', msg: '' }); }}
            onPick={(s, at) => { setNewSymbol(s); if (at) setNewSymbolAsset(at); setAddCheck({ status: 'idle', msg: '' }); }}
            onKeyDownExtra={(e) => { if (e.key === 'Enter') addUniverseSymbol(); }}
            classes={newSymbolAsset}
            placeholder="+ add symbol"
            wrapperStyle={{ width: 110 }}
            menuMinWidth={240}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '4px 8px', fontSize: 12,
              border: '1px solid var(--border)', borderRadius: 6,
              background: 'var(--card)', color: 'var(--text)', textTransform: 'uppercase',
            }}
            invalid={addCheck.status === 'invalid'}
            invalidStyle={{ border: '1px solid var(--red, #f85149)' }}
          />
          <button
            onClick={addUniverseSymbol}
            disabled={!newSymbol.trim() || addCheck.status === 'checking'}
            style={{
              padding: '4px 12px', fontSize: 12, fontWeight: 600,
              border: '1px solid var(--border)', borderRadius: 6,
              background: 'var(--card)', color: 'var(--text)',
              cursor: (newSymbol.trim() && addCheck.status !== 'checking') ? 'pointer' : 'not-allowed',
            }}
          >{addCheck.status === 'checking' ? 'Checking…' : 'Add'}</button>
          {addCheck.status === 'invalid' && (
            <span style={{ fontSize: 11, color: 'var(--red, #f85149)', flexBasis: '100%' }}>{addCheck.msg}</span>
          )}
          {addCheck.status === 'invalid' && addCheck.data && (
            <div style={{ flexBasis: '100%' }}>
              <SymbolSuggestion
                result={addCheck.data}
                compact
                onApply={(s, at) => {
                  setNewSymbol(s);
                  if (at) setNewSymbolAsset(at);
                  setAddCheck({ status: 'idle', msg: '' });
                }}
              />
            </div>
          )}
        </div>

        {/* AI Generation Panel */}
        {showAiPanel && !chatMode && (
          <div style={{
            padding: '12px 16px',
            background: 'var(--card)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
          }}>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={hasExistingGraph
                ? "Describe how to change this strategy… e.g. 'Add a stop-loss when RSI exceeds 80' or 'Switch from SMA to EMA'"
                : "Describe your strategy in plain English… e.g. 'Buy when RSI crosses below 30, sell when it crosses above 70'"}
              rows={2}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 13,
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: 'var(--surface)',
                color: 'var(--text)',
                resize: 'vertical',
                fontFamily: 'var(--font-body)',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleGenerate();
              }}
            />
            <button
              onClick={handleGenerate}
              disabled={generating || !aiPrompt.trim()}
              style={{
                padding: '8px 20px',
                background: generating ? 'var(--muted)' : 'var(--amber)',
                color: 'var(--bg)',
                border: 'none',
                borderRadius: 6,
                cursor: generating ? 'wait' : 'pointer',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {generating ? <><Spinner size={11} color="var(--bg)" style={{ marginRight: 6 }} />{hasExistingGraph ? 'Updating…' : 'Generating…'}</> : (hasExistingGraph ? 'Update' : 'Generate')}
            </button>
          </div>
        )}

        {/* Cannot-build banner */}
        {cannotBuildInfo && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(251,191,36,0.1)',
            borderBottom: '1px solid rgba(251,191,36,0.35)',
            fontSize: 13,
            color: 'var(--text)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>🤔</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--amber)' }}>
                  The AI couldn't build this strategy with the current blocks
                </div>
                <div style={{ color: 'var(--muted)', marginBottom: 8, lineHeight: 1.5 }}>
                  {cannotBuildInfo.detail}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>Would you like to see this supported?</span>
                  <button
                    onClick={() => {
                      setCannotBuildInfo(null);
                      if (onFeedback) onFeedback(
                        `Feature Request: ${cannotBuildInfo.prompt.slice(0, 120)}`,
                        `I tried to build the following strategy but the AI said it wasn't possible with the current blocks:\n\n"${cannotBuildInfo.prompt}"\n\nAI response:\n${cannotBuildInfo.detail}`
                      );
                    }}
                    style={{ padding: '5px 14px', background: 'var(--amber)', color: 'var(--bg)', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
                  >
                    Request this feature →
                  </button>
                </div>
              </div>
              <button
                onClick={() => setCannotBuildInfo(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)', flexShrink: 0 }}
              >✕</button>
            </div>
          </div>
        )}

        {/* Error banner */}
        {saveError && (
          <div style={{
            padding: '10px 16px',
            background: 'rgba(248,81,73,0.12)',
            borderBottom: '1px solid rgba(248,81,73,0.3)',
            fontSize: 13,
            color: '#f85149',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ flex: 1 }}>⚠️ {saveError}</span>
            <button
              onClick={() => navigate('/pricing')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#f85149', fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}
            >View plans →</button>
            <button
              onClick={() => setSaveError('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#f85149' }}
            >✕</button>
          </div>
        )}

        {/* Translated-from-code warning banner */}
        {fromCodeGen && (
          <div style={{
            padding: '10px 16px',
            background: 'rgba(242,182,50,0.12)',
            borderBottom: '1px solid rgba(242,182,50,0.4)',
            fontSize: 13,
            color: '#e3a008',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ flex: 1 }}>
              ⚠️ Translated from pasted code — this is a close approximation, not an exact port.
              Review the entry/exit logic against your original before trusting the backtest.
            </span>
            <button
              onClick={() => setFromCodeGen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)' }}
            >✕</button>
          </div>
        )}

        {/* Brief explanation banner */}
        {briefExplanation && (
          <div style={{
            padding: '10px 16px',
            background: 'var(--card)',
            borderBottom: '1px solid var(--border)',
            fontSize: 13,
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ flex: 1 }}>{briefExplanation}</span>
            <button
              onClick={() => setBriefExplanation(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)' }}
            >✕</button>
          </div>
        )}

        {/* Canvas — replaced full-screen by the chat panel on mobile while chat mode is active */}
        {chatMode && isMobile ? (
          <BuilderChatPanel
            isMobile
            onGraphUpdate={handleChatGraphUpdate}
            onClose={() => setChatMode(false)}
            onRunBacktest={onRunBacktest}
            onViewResults={onViewResults}
          />
        ) : (
          <div ref={reactFlowWrapper} className="builder-canvas" data-tour="canvas" style={{ flex: 1 }}>
            <ReactFlow
              nodes={displayNodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={customNodeTypes}
              fitView
              deleteKeyCode="Backspace"
              proOptions={{ hideAttribution: true }}
            >
              <Background variant="dots" gap={16} size={1} color="#30363d" />
              <Controls />
              {!isMobile && <MiniMap zoomable pannable style={{ background: '#161b22' }} />}
            </ReactFlow>
          </div>
        )}
      </div>

      {/* Chat panel sits alongside the canvas on desktop */}
      {chatMode && !isMobile && (
        <BuilderChatPanel onGraphUpdate={handleChatGraphUpdate} isMobile={false} onRunBacktest={onRunBacktest} onViewResults={onViewResults} />
      )}

      {showExport && (
        <ExportStrategyModal
          graph={buildGraph()}
          name={strategyName}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
   </UniverseContext.Provider>
  );
}
